/**
 * ACT2-1124: the activation accounts of the workspaces that Lookout can read (Settings › General, D18), for the
 * Accounts list of a sibling workspace. The server reads them with the service user and sends no secret. Copy
 * (`useAccountCopyPick`) makes one of them an account of this workspace, and its row then goes.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { orpcErrorMessage } from '@/_shared/lib/orpc-errors';

import { type OrganizationAccountListRow, organizationAccountRows } from '../../lib/accountList';

export interface OrganizationDestinationAccounts {
  rows: OrganizationAccountListRow[];
  isLoading: boolean;
  /** Lookout could not read the organization's accounts; the list then shows only this workspace's own. */
  unavailable: string | null;
  /** How many workspaces the readable list holds, once it is read. Zero means that no account of another one shows. */
  readableWorkspaceCount: number | null;
}

/** Stable, so the list's memo does not rebuild on every render while a query has no data. */
const NO_ROWS: OrganizationAccountListRow[] = [];

export function useOrganizationDestinationAccounts(options: {
  enabled: boolean;
  /** The ids of this workspace's own accounts: a copy among them stands in for its original. */
  ownAccountIds: ReadonlySet<string>;
}): OrganizationDestinationAccounts {
  const orpc = useOrpc();
  const optionsQuery = useQuery({
    ...orpc.accountCopies.listOptions.queryOptions({ input: { kind: 'destination' } }),
    enabled: options.enabled,
  });
  // The select scope, as the Add drawer asks it: a copy is a new account of this workspace.
  const definitionsQuery = useQuery({
    ...orpc.settings.rudderstackAccounts.listDefinitions.queryOptions({ input: {} }),
    enabled: options.enabled,
  });
  const offeredDefinitions = useMemo(
    () => new Map((definitionsQuery.data?.definitions ?? []).map((definition) => [definition.name, definition])),
    [definitionsQuery.data],
  );
  const accounts = optionsQuery.data?.accounts;
  const rows = useMemo(
    () => (accounts ? organizationAccountRows(accounts, offeredDefinitions, options.ownAccountIds) : NO_ROWS),
    [accounts, offeredDefinitions, options.ownAccountIds],
  );
  const failed = optionsQuery.isError ? optionsQuery.error : definitionsQuery.isError ? definitionsQuery.error : null;

  return {
    rows,
    isLoading: options.enabled && (optionsQuery.isPending || definitionsQuery.isPending),
    unavailable: failed ? orpcErrorMessage(failed, 'Could not read the accounts of other workspaces.') : null,
    readableWorkspaceCount: optionsQuery.data?.readableWorkspaceCount ?? null,
  };
}
